import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  useTheme,
} from '@mui/material';
import { CheckCircle as ApproveIcon, Cancel as RejectIcon } from '@mui/icons-material';
import Layout from '../components/Layout';

const ChangeRequests = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewComment, setReviewComment] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get('/api/change-requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Ошибка загрузки заявок:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId, status) => {
    try {
      await axios.patch(`/api/change-requests/${requestId}`, {
        status,
        reviewComment,
      });
      await fetchRequests();
      setSelectedRequest(null);
      setReviewComment('');
    } catch (error) {
      console.error('Ошибка обработки заявки:', error);
    }
  };

  const fieldLabels = {
    name: 'Название',
    address: 'Адрес',
    phone: 'Телефон',
    contactPerson: 'Контактное лицо',
    email: 'Email',
    status: 'Статус',
    color: 'Цвет',
    additionalInfo: 'Дополнительная информация',
    requisites: 'Реквизиты',
    inn: 'ИНН',
    kpp: 'КПП',
    ogrn: 'ОГРН',
    bankName: 'Банк',
    bik: 'БИК',
    checkingAccount: 'Расчетный счет',
    correspondentAccount: 'Корреспондентский счет'
  };

  const renderChangeDetails = (request) => {
    const { oldValues = {}, newValues = {} } = request;
    
    return (
      <Box>
        {Object.keys(newValues).map((key) => {
          if (key === 'requisites' && typeof newValues[key] === 'object') {
            return (
              <Box 
                key={key} 
                mb={3} 
                p={2} 
                sx={{ 
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} mb={2} color="text.primary">
                  {fieldLabels[key]}:
                </Typography>
                {Object.keys(newValues.requisites).map((reqKey) => (
                  <Box key={reqKey} mb={2}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {fieldLabels[reqKey]}:
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Box 
                        flex={1} 
                        p={1.5} 
                        sx={{ 
                          bgcolor: 'rgba(191, 97, 106, 0.1)', 
                          borderRadius: 2, 
                          border: '1px solid rgba(191, 97, 106, 0.3)' 
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#bf616a', fontWeight: 600 }}>БЫЛО:</Typography>
                        <Typography variant="body2" color="text.primary">{oldValues.requisites?.[reqKey] || '(пусто)'}</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>→</Typography>
                      <Box 
                        flex={1} 
                        p={1.5} 
                        sx={{ 
                          bgcolor: 'rgba(163, 190, 140, 0.1)', 
                          borderRadius: 2, 
                          border: '1px solid rgba(163, 190, 140, 0.3)' 
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#a3be8c', fontWeight: 600 }}>СТАЛО:</Typography>
                        <Typography variant="body2" color="text.primary">{newValues.requisites[reqKey] || '(пусто)'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          }
          
          return (
            <Box key={key} mb={2}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {fieldLabels[key] || key}:
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Box 
                  flex={1} 
                  p={1.5} 
                  sx={{ 
                    bgcolor: 'rgba(191, 97, 106, 0.1)', 
                    borderRadius: 2, 
                    border: '1px solid rgba(191, 97, 106, 0.3)' 
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#bf616a', fontWeight: 600 }}>БЫЛО:</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', color: 'text.primary' }}>
                    {oldValues[key] || '(пусто)'}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>→</Typography>
                <Box 
                  flex={1} 
                  p={1.5} 
                  sx={{ 
                    bgcolor: 'rgba(163, 190, 140, 0.1)', 
                    borderRadius: 2, 
                    border: '1px solid rgba(163, 190, 140, 0.3)' 
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#a3be8c', fontWeight: 600 }}>СТАЛО:</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', color: 'text.primary' }}>
                    {newValues[key] || '(пусто)'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка заявок...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          📝 Заявки на изменение компаний
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Ожидает одобрения: {requests.length}
        </Typography>
      </Box>

      {requests.length === 0 ? (
        <Card
          sx={{
            background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
            backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
          }}
        >
          <CardContent>
            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Нет ожидающих заявок
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {requests.map((request, index) => (
            <Grid item xs={12} md={6} key={request._id}>
              <Card
                sx={{
                  background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
                  backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                  border: '1px solid',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  animation: `fadeInUp 0.4s ease ${index * 0.1}s both`,
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(20px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDarkMode 
                      ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                      : '0 12px 40px rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {request.company?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        От: {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {new Date(request.createdAt).toLocaleString('ru-RU')}
                      </Typography>
                    </Box>
                    <Chip 
                      label="Ожидает" 
                      size="small"
                      sx={{
                        background: 'rgba(235, 203, 139, 0.2)',
                        color: '#ebcb8b',
                        border: '1px solid rgba(235, 203, 139, 0.3)',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2, opacity: 0.3 }} />

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setSelectedRequest(request)}
                    sx={{ mb: 1 }}
                  >
                    Просмотреть изменения
                  </Button>

                  <Box display="flex" gap={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleReview(request._id, 'approved')}
                      sx={{
                        background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #88c0d0 0%, #a3be8c 100%)',
                        }
                      }}
                    >
                      Одобрить
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<RejectIcon />}
                      onClick={() => {
                        setSelectedRequest(request);
                      }}
                      sx={{
                        background: 'linear-gradient(135deg, #bf616a 0%, #d08770 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d08770 0%, #bf616a 100%)',
                        }
                      }}
                    >
                      Отклонить
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Диалог просмотра изменений */}
      <Dialog
        open={!!selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
          setReviewComment('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Предлагаемые изменения</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>
                <strong>Компания:</strong> {selectedRequest.company?.name}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2 }}>
                <strong>От:</strong> {selectedRequest.requestedBy?.firstName}{' '}
                {selectedRequest.requestedBy?.lastName}
              </Typography>

              <Divider sx={{ my: 2, opacity: 0.3 }} />

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                Изменения:
              </Typography>

              {renderChangeDetails(selectedRequest)}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Комментарий (необязательно)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                margin="normal"
                placeholder="Добавьте комментарий к решению..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSelectedRequest(null);
              setReviewComment('');
            }}
            variant="outlined"
          >
            Закрыть
          </Button>
          <Button
            variant="contained"
            startIcon={<RejectIcon />}
            onClick={() => handleReview(selectedRequest._id, 'rejected')}
            sx={{
              background: 'linear-gradient(135deg, #bf616a 0%, #d08770 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d08770 0%, #bf616a 100%)',
              }
            }}
          >
            Отклонить
          </Button>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => handleReview(selectedRequest._id, 'approved')}
            sx={{
              background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #88c0d0 0%, #a3be8c 100%)',
              }
            }}
          >
            Одобрить
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ChangeRequests;
